const GlobalAudioContext = {
  activeElement: null,
};

const APILoader = {
  yt: { loading: false, callbacks: [] },
  vimeo: { loading: false, callbacks: [] },
  loadYouTube(cb) {
    if (window.YT && window.YT.Player) return cb();
    this.yt.callbacks.push(cb);
    if (!this.yt.loading) {
      this.yt.loading = true;
      const script = document.createElement('script');
      script.src = 'https://www.youtube.com/iframe_api';
      window.onYouTubeIframeAPIReady = () => {
        this.yt.callbacks.forEach((c) => c());
      };
      document.head.appendChild(script);
    }
  },
  loadVimeo(cb) {
    if (window.Vimeo && window.Vimeo.Player) return cb();
    this.vimeo.callbacks.push(cb);
    if (!this.vimeo.loading) {
      this.vimeo.loading = true;
      const script = document.createElement('script');
      script.src = 'https://player.vimeo.com/api/player.js';
      script.onload = () => {
        this.vimeo.callbacks.forEach((c) => c());
      };
      document.head.appendChild(script);
    }
  },
};

const upgradeYoutubePosters = () => {
  document.querySelectorAll('img[data-youtube-poster][data-youtube-id]').forEach((img) => {
    const id = img.getAttribute('data-youtube-id');
    if (!id) return;
    const maxres = `https://i.ytimg.com/vi/${id}/maxresdefault.jpg`;
    const probe = new Image();
    probe.onload = () => {
      if (probe.naturalWidth > 120) img.src = maxres;
    };
    probe.src = maxres;
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', upgradeYoutubePosters);
} else {
  upgradeYoutubePosters();
}

const YoutubeVideoModal = {
  el: null,

  getEl() {
    if (this.el) return this.el;

    const wrap = document.createElement('div');
    wrap.className = 'youtube-video-modal css-reset tailwind-layout';
    wrap.setAttribute('hidden', '');
    wrap.innerHTML = `
      <div class="youtube-video-modal__dialog" data-yt-dialog>
        <button type="button" class="youtube-video-modal__close" data-yt-close aria-label="Close">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
            <path d="M10.918 0.919237L0.917969 10.9192M10.918 10.9192L0.917969 0.919235" stroke="currentColor" stroke-width="1.3" stroke-linecap="square"/>
          </svg>
        </button>
        <div class="youtube-video-modal__mount" data-yt-mount></div>
      </div>
    `;

    wrap.addEventListener('click', (e) => {
      if (e.target === wrap) this.close();
    });
    wrap.querySelector('[data-yt-close]').addEventListener('click', () => this.close());
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.el && !this.el.hasAttribute('hidden')) this.close();
    });

    document.body.appendChild(wrap);
    this.el = wrap;
    return wrap;
  },

  open(videoId) {
    const id = String(videoId || '').replace(/[^a-zA-Z0-9_-]/g, '');
    if (!id) return;
    const wrap = this.getEl();
    wrap.removeAttribute('hidden');
    const mount = wrap.querySelector('[data-yt-mount]');
    mount.innerHTML = `<video-intersection-insert
      class="relative block h-full w-full"
      data-provider="youtube"
      data-video-id="${id}"
      data-muted="false"
      data-controls="true"
      data-is-modal="true"
    ></video-intersection-insert>`;
    mount.querySelector('video-intersection-insert')?.loadMedia?.();
  },

  close() {
    if (!this.el) return;
    const player = this.el.querySelector('video-intersection-insert');
    player?.pauseMedia?.();
    player?.destroyMedia?.();
    const mount = this.el.querySelector('[data-yt-mount]');
    if (mount) mount.innerHTML = '';
    this.el.setAttribute('hidden', '');
  },
};

document.addEventListener(
  'click',
  (e) => {
    const trigger = e.target.closest('[data-youtube-modal-open]');
    if (!trigger) return;
    e.preventDefault();
    e.stopPropagation();
    YoutubeVideoModal.open(trigger.getAttribute('data-youtube-modal-open'));
  },
  true,
);

class VideoIntersectionInsert extends HTMLElement {
  constructor() {
    super();
    this._observer = null;
    this._isPlaying = false;
    this._loaded = false;
    this._ready = false;
    this._pendingPlay = false;
    this._player = null;
  }

  connectedCallback() {
    this._provider = this.dataset.provider || 'html5';
    this._videoId = this.dataset.videoId;
    this._muted = this.dataset.muted === 'true';
    this._controls = this.dataset.controls === 'true';
    this._isModal = this.dataset.isModal === 'true';
    this._pausedOnLoad = this.dataset.pausedOnLoad === 'true';

    if (!this._videoId && !this.dataset.src) return;

    this._observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        const isVisible = entry.isIntersecting;

        if (isVisible && !this._loaded) {
          this.loadMedia();
        }

        if (this._pausedOnLoad) {
          if (!isVisible && this._isPlaying) {
            this.pauseMedia();
          }
        } else {
          if (isVisible && this._loaded && !this._isPlaying) {
            if (this._muted || this._isModal) {
              this.playMedia();
            }
          } else if (!isVisible && this._isPlaying) {
            this.handleViewportExit();
          }
        }
      },
      {
        root: null,
        rootMargin: '200px',
        threshold: [0],
      },
    );

    this._observer.observe(this);
  }

  handleViewportExit() {
    this.pauseMedia();
    if (!this._muted) {
      this.muteMedia();
    }
  }

  loadMedia() {
    if (this._loaded) return;
    this.innerHTML = '';
    this._loaded = true;

    if (this._provider === 'youtube') {
      const container = document.createElement('div');
      container.className = 'w-full h-full';
      if (!this._controls && this._muted) container.style.pointerEvents = 'none';
      this.appendChild(container);
      APILoader.loadYouTube(() => this.initYouTube(container));
    } else if (this._provider === 'vimeo') {
      const container = document.createElement('div');
      container.className = 'w-full h-full';
      if (!this._controls && this._muted) container.style.pointerEvents = 'none';
      this.appendChild(container);
      APILoader.loadVimeo(() => this.initVimeo(container));
    } else {
      this.loadHTML5();
    }
  }

  loadHTML5() {
    if (this.dataset.src) {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = this.dataset.src;
      this._player = tempDiv.querySelector('video');
      this.appendChild(this._player);
    } else {
      this._player = document.createElement('video');
      this._player.src = this._videoId;
      this._player.className = this.dataset.class || '';
      this._player.loop = true;
      this._player.playsInline = true;
      if (this._muted) this._player.muted = true;
      if (this._controls) {
        this._player.controls = true;
      }
      this.appendChild(this._player);
    }

    if (!this._controls) {
      this._player.removeAttribute('controls');
      if (this._muted) {
        this._player.style.pointerEvents = 'none';
      } else {
        this._player.style.cursor = 'pointer';
        this._player.addEventListener('click', () => {
          if (this._isPlaying) {
            this.pauseMedia();
          } else {
            this.playMedia();
          }
        });
      }
    }

    if (this._muted) {
      if (this._player.readyState >= 1) {
        this.playMedia();
      } else {
        this._player.addEventListener(
          'loadedmetadata',
          () => {
            this.playMedia();
          },
          { once: true },
        );
      }
    }

    this._player.addEventListener('play', () => {
      this._isPlaying = true;
    });
    this._player.addEventListener('pause', () => {
      this._isPlaying = false;
    });
  }

  initYouTube(container) {
    const autoplayFlag = !this._pausedOnLoad && (this._muted || this._isModal) ? 1 : 0;

    // 动态判断：有控制条就给完整原生体验，无控制条则极限精简
    const playerVars = {
      autoplay: autoplayFlag,
      controls: this._controls ? 1 : 0,
      mute: this._muted ? 1 : 0,
      loop: 1,
      playlist: this._videoId,
      playsinline: 1,
      rel: 0,
    };

    if (!this._controls) {
      playerVars.modestbranding = 1;
      playerVars.disablekb = 1;
      playerVars.iv_load_policy = 3;
    }

    this._player = new YT.Player(container, {
      videoId: this._videoId,
      width: '100%', // 强制满宽满高，修复 UI 控制栏被切除的 Bug
      height: '100%',
      playerVars: playerVars,
      events: {
        onReady: () => {
          this._ready = true;
          if (this._pendingPlay) {
            this._pendingPlay = false;
            this.playMedia();
          } else if (!this._pausedOnLoad && (this._muted || this._isModal)) {
            this.playMedia();
          }
        },
        onStateChange: (e) => {
          if (e.data === YT.PlayerState.PLAYING) {
            this._isPlaying = true;
          } else if (e.data === YT.PlayerState.PAUSED) {
            this._isPlaying = false;
          }
        },
      },
    });
  }

  initVimeo(container) {
    this._player = new Vimeo.Player(container, {
      id: this._videoId,
      width: '100%',
      height: '100%',
      autoplay: this._muted && !this._pausedOnLoad,
      controls: this._controls,
      muted: this._muted,
      loop: true,
      background: !this._controls && this._muted && !this._pausedOnLoad,
      // 动态判断：只有 controls: true 时才显示原生标题和头像信息
      title: this._controls,
      byline: this._controls,
      portrait: this._controls,
      dnt: true,
    });

    this._player.ready().then(() => {
      this._ready = true;
      if (this._pendingPlay) {
        this._pendingPlay = false;
        this.playMedia();
      } else if (!this._pausedOnLoad && (this._muted || this._isModal)) {
        this.playMedia();
      }
    });

    this._player.on('play', () => {
      this._isPlaying = true;
    });
    this._player.on('pause', () => {
      this._isPlaying = false;
    });
  }

  playMedia() {
    if ((this._provider === 'youtube' || this._provider === 'vimeo') && !this._ready) {
      if (!this._loaded) this.loadMedia();
      this._pendingPlay = true;
      return;
    }

    if (!this._muted) {
      if (GlobalAudioContext.activeElement && GlobalAudioContext.activeElement !== this) {
        GlobalAudioContext.activeElement.muteMedia();
      }
      GlobalAudioContext.activeElement = this;
    }

    if (this._provider === 'html5' && this._player) {
      const playPromise = this._player.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            this._isPlaying = true;
            if (!this._muted) this._player.muted = false;
          })
          .catch(() => {
            this._isPlaying = false;
          });
      }
    } else if (this._provider === 'youtube' && this._player?.playVideo) {
      this._player.playVideo();
      if (!this._muted) this._player.unMute();
      this._isPlaying = true;
    } else if (this._provider === 'vimeo' && this._player?.play) {
      this._player.play();
      if (!this._muted) this._player.setVolume(1);
      this._isPlaying = true;
    }
  }

  pauseMedia() {
    if (this._provider === 'html5' && this._player) {
      this._player.pause();
    } else if (this._provider === 'youtube' && this._player?.pauseVideo) {
      this._player.pauseVideo();
    } else if (this._provider === 'vimeo' && this._player?.pause) {
      this._player.pause();
    }
    this._isPlaying = false;
  }

  muteMedia() {
    if (this._provider === 'html5' && this._player) {
      this._player.muted = true;
    } else if (this._provider === 'youtube' && this._player?.mute) {
      this._player.mute();
    } else if (this._provider === 'vimeo' && this._player?.setVolume) {
      this._player.setVolume(0);
    }

    if (GlobalAudioContext.activeElement === this) {
      GlobalAudioContext.activeElement = null;
    }
  }

  resetMedia() {
    if (this._provider === 'html5' && this._player) {
      this._player.currentTime = 0;
    } else if (this._provider === 'youtube' && this._player?.seekTo) {
      this._player.seekTo(0, true);
    } else if (this._provider === 'vimeo' && this._player?.setCurrentTime) {
      this._player.setCurrentTime(0);
    }
  }

  destroyMedia() {
    this.pauseMedia();
    this._pendingPlay = false;
    this._loaded = false;
    this._ready = false;

    if (this._provider === 'html5' && this._player) {
      this._player.pause();
      this._player.removeAttribute('src');
      this._player.load();
    } else if (this._provider === 'youtube' && this._player?.destroy) {
      try {
        this._player.destroy();
      } catch (e) {}
    } else if (this._provider === 'vimeo' && this._player?.unload) {
      this._player.unload();
    }

    if (GlobalAudioContext.activeElement === this) {
      GlobalAudioContext.activeElement = null;
    }
    this._player = null;
  }

  disconnectedCallback() {
    this._observer?.disconnect();
    this.destroyMedia();
  }
}

if (!customElements.get('video-intersection-insert')) {
  customElements.define('video-intersection-insert', VideoIntersectionInsert);
}

class productMediaSwiper extends HTMLElement {
  connectedCallback() {
    this._inView = true;
    this._userPaused = false;
    this._onZoomOpen = () => this.pauseAllMedia();
    window.addEventListener('open-product-zoom', this._onZoomOpen);
    this.bindVideoControls();
    this.bindViewportObserver();
    this.bindThumbs();
    this.bindThumbPlacement();
    this.initSwiper();
  }

  disconnectedCallback() {
    window.removeEventListener('open-product-zoom', this._onZoomOpen);
    this._desktopQuery?.removeEventListener('change', this._onDesktopChange);
    this._thumbPlacementObserver?.disconnect();
    this._observer?.disconnect();
    this.bindActiveVideo(null);
  }

  getActiveVideo() {
    const swiper = this.querySelector('.swiper')?.swiper;
    const activeSlide = swiper?.slides?.[swiper.activeIndex];
    return activeSlide?.querySelector('video') || null;
  }

  bindViewportObserver() {
    this._observer = new IntersectionObserver(
      ([entry]) => {
        this._inView = entry.isIntersecting;
        if (!this._inView) {
          this.pauseAllMedia();
          this.updateVideoUi(this.getActiveVideo());
          return;
        }
        this.playActiveVideo();
      },
      { threshold: 0 },
    );
    this._observer.observe(this);
  }

  bindVideoControls() {
    this.playBtn = this.querySelector('[data-video-play]');
    this.playCenterBtn = this.querySelector('[data-video-play-center]');
    this.muteBtn = this.querySelector('[data-video-mute]');
    this.controls = this.querySelector('[data-video-controls]');
    this.progress = this.querySelector('[data-video-progress]');
    this.progressWrap = this.querySelector('[data-video-progress-wrap]');
    this._seeking = false;
    this._onTimeUpdate = () => this.updateProgress();
    this._onPlayPause = () => this.updateVideoUi(this.getActiveVideo());

    this.playBtn?.addEventListener('click', (event) => {
      event.stopPropagation();
      this.toggleActiveVideo();
    });
    this.playCenterBtn?.addEventListener('click', (event) => {
      event.stopPropagation();
      this.toggleActiveVideo();
    });

    this.muteBtn?.addEventListener('click', (event) => {
      event.stopPropagation();
      const video = this.getActiveVideo();
      if (!video) return;
      video.muted = !video.muted;
      this.updateVideoUi(video);
    });

    this.progress?.addEventListener('pointerdown', (event) => {
      event.stopPropagation();
      this._seeking = true;
    });
    this.progress?.addEventListener('input', (event) => {
      event.stopPropagation();
      this._seeking = true;
      const video = this.getActiveVideo();
      if (!video || !video.duration) return;
      const ratio = Number(event.target.value) / 1000;
      video.currentTime = ratio * video.duration;
      this.setProgress(ratio);
    });
    const endSeek = () => {
      this._seeking = false;
    };
    this.progress?.addEventListener('pointerup', endSeek);
    this.progress?.addEventListener('change', endSeek);
  }

  toggleActiveVideo() {
    const video = this.getActiveVideo();
    if (!video) return;
    if (video.paused) {
      this._userPaused = false;
      video.play().catch(() => {});
    } else {
      this._userPaused = true;
      video.pause();
    }
    this.updateVideoUi(video);
  }

  bindActiveVideo(video) {
    if (this._boundVideo === video) return;
    this._boundVideo?.removeEventListener('timeupdate', this._onTimeUpdate);
    this._boundVideo?.removeEventListener('play', this._onPlayPause);
    this._boundVideo?.removeEventListener('pause', this._onPlayPause);
    this._boundVideo = video || null;
    if (!video) {
      this.setProgress(0);
      return;
    }
    video.addEventListener('timeupdate', this._onTimeUpdate);
    video.addEventListener('play', this._onPlayPause);
    video.addEventListener('pause', this._onPlayPause);
    this.updateProgress();
  }

  updateProgress() {
    if (this._seeking) return;
    const video = this.getActiveVideo();
    if (!video || !video.duration) {
      this.setProgress(0);
      return;
    }
    this.setProgress(video.currentTime / video.duration);
  }

  setProgress(ratio) {
    const clamped = Math.max(0, Math.min(1, ratio || 0));
    if (this.progress) this.progress.value = String(Math.round(clamped * 1000));
    this.progress?.style.setProperty('--progress', `${clamped * 100}%`);
  }

  updateVideoUi(video) {
    const hasVideo = Boolean(video);
    const playing = hasVideo && !video.paused;
    this.controls?.classList.toggle('hidden', !hasVideo);
    this.controls?.classList.toggle('flex', hasVideo);
    this.playCenterBtn?.classList.toggle('hidden', !hasVideo || playing);
    this.playCenterBtn?.classList.toggle('flex', hasVideo && !playing);
    this.progressWrap?.classList.toggle('hidden', !hasVideo);
    if (!hasVideo) {
      this.bindActiveVideo(null);
      return;
    }

    this.bindActiveVideo(video);
    this.playBtn?.querySelector('[data-icon="play"]')?.classList.toggle('hidden', playing);
    this.playBtn?.querySelector('[data-icon="pause"]')?.classList.toggle('hidden', !playing);
    this.playBtn?.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
    this.muteBtn?.querySelector('[data-icon="muted"]')?.classList.toggle('hidden', !video.muted);
    this.muteBtn?.querySelector('[data-icon="unmuted"]')?.classList.toggle('hidden', video.muted);
    this.muteBtn?.setAttribute('aria-label', video.muted ? 'Unmute video' : 'Mute video');
  }

  pauseAllMedia() {
    this.querySelectorAll('video').forEach((video) => {
      video.pause();
    });
    this.querySelectorAll('iframe').forEach((iframe) => {
      const src = iframe.getAttribute('src') || '';
      if (src.includes('youtube') || src.includes('youtu.be')) {
        iframe.contentWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
      } else if (src.includes('vimeo')) {
        iframe.contentWindow?.postMessage('{"method":"pause"}', '*');
      }
    });
  }

  playActiveVideo() {
    const video = this.getActiveVideo();
    this.updateVideoUi(video);
    if (!video || !this._inView || this._userPaused) return;
    const playPromise = video.play();
    if (playPromise !== undefined) {
      playPromise.then(() => this.updateVideoUi(video)).catch(() => this.updateVideoUi(video));
    }
  }

  syncMedia() {
    this.pauseAllMedia();
    this._userPaused = false;
    this.playActiveVideo();
    const swiper = this.querySelector('.swiper')?.swiper;
    if (swiper) this.syncThumbs(swiper.realIndex);
  }

  bindThumbs() {
    this.querySelectorAll('[data-media-thumb]').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.stopPropagation();
        const index = Number(button.dataset.index);
        this.querySelector('.swiper')?.swiper?.slideTo(index);
      });
    });
  }

  syncThumbs(index) {
    const thumbs = [...this.querySelectorAll('[data-media-thumb]')];
    const track = this.querySelector('[data-media-thumbs-track]');
    const viewport = this.querySelector('[data-media-thumbs]');
    if (!thumbs.length || !track || !viewport) return;

    thumbs.forEach((thumb, thumbIndex) => {
      const active = thumbIndex === index;
      thumb.classList.toggle('is-active', active);
      thumb.setAttribute('aria-current', active ? 'true' : 'false');
    });

    const thumbWidth = thumbs[0].getBoundingClientRect().width;
    const gap = Number.parseFloat(getComputedStyle(track).columnGap) || 0;
    const step = thumbWidth + gap;
    if (!step || !viewport.clientWidth) {
      this.placeThumbs();
      return;
    }

    const visible = Math.max(1, Math.round((viewport.clientWidth + gap) / step));
    const maxStart = Math.max(0, thumbs.length - visible);
    const start = Math.min(Math.floor(index / visible) * visible, maxStart);

    this._thumbStart = start;
    track.style.transform = `translateX(-${start * step}px)`;
    this.placeThumbs();
  }

  bindThumbPlacement() {
    const swiperEl = this.querySelector('.swiper');
    if (!swiperEl || this._thumbPlacementObserver) return;
    this._thumbPlacementObserver = new ResizeObserver(() => this.placeThumbs());
    this._thumbPlacementObserver.observe(swiperEl);
  }

  placeThumbs() {
    const thumbs = this.querySelector('[data-media-thumbs]');
    const swiperEl = this.querySelector('.swiper');
    const swiper = swiperEl?.swiper;
    if (!thumbs || !swiperEl || !swiper) return;

    const slide = swiper.slides?.[swiper.activeIndex];
    const media = slide?.querySelector('img, video');
    if (!media) {
      thumbs.style.bottom = '24px';
      return;
    }

    const position = () => {
      const box = media.getBoundingClientRect();
      const swiperBox = swiperEl.getBoundingClientRect();
      const naturalWidth = media.naturalWidth || media.videoWidth;
      const naturalHeight = media.naturalHeight || media.videoHeight;
      if (!naturalWidth || !naturalHeight || !box.width || !box.height) {
        thumbs.style.bottom = '24px';
        return;
      }

      const imageRatio = naturalWidth / naturalHeight;
      const boxRatio = box.width / box.height;
      const contentHeight = boxRatio > imageRatio ? box.height : box.width / imageRatio;
      const gapBelowImage = (box.height - contentHeight) / 2;
      const contentBottom = box.bottom - gapBelowImage;
      const offsetFromSwiperBottom = swiperBox.bottom - contentBottom;
      thumbs.style.bottom = `${Math.max(0, offsetFromSwiperBottom + 24)}px`;
    };

    if (media instanceof HTMLImageElement && !media.complete) {
      media.addEventListener('load', position, { once: true });
    } else if (media instanceof HTMLVideoElement && !media.videoWidth) {
      media.addEventListener('loadedmetadata', position, { once: true });
    }
    position();
  }

  initSwiper() {
    const desktopQuery = window.matchMedia('(min-width: 1000px)');
    this._desktopQuery = desktopQuery;
    this._onDesktopChange = () => this.mountSwiper();
    desktopQuery.addEventListener('change', this._onDesktopChange);
    this.mountSwiper();
  }

  mountSwiper() {
    const swiperEl = this.querySelector('.swiper');
    swiperEl?.swiper?.destroy(true, true);
    const loop = !this._desktopQuery.matches;
    new Swiper(swiperEl, {
      observer: true,
      observeParents: true,
      slidesPerView: 1,
      spaceBetween: 8,
      loop,
      loopAdditionalSlides: loop ? 3 : 0,
      noSwipingSelector: 'input, button, [data-media-thumbs]',
      on: {
        init: () => this.syncMedia(),
        slideChange: () => this.syncMedia(),
        tap: (_swiper, event) => {
          if (event.target.closest('button, input, .pagination, [data-media-thumbs]')) return;
          if (!this.getActiveVideo()) return;
          this.toggleActiveVideo();
        },
      },
      breakpoints: {
        1000: {
          centeredSlides: true,
          spaceBetween: 12,
        },
      },
      pagination: {
        el: this.querySelector('.pagination'),
        clickable: true,
        renderBullet: function (index, className) {
          const bulletClasses = `
              ${className}
              rounded-none
              w-8 h-8 bg-[#CCCCCC] transition-all duration-300 block cursor-pointer
              hover:bg-white
              [&.swiper-pagination-bullet-active]:w-50 [&.swiper-pagination-bullet-active]:h-8 [&.swiper-pagination-bullet-active]:bg-[#FEDB1E]
            `;
          return `<span class="${bulletClasses.trim().replace(/\s+/g, ' ')}"></span>`;
        },
      },
      navigation: {
        nextEl: this.querySelector('.swiper-button-next'),
        prevEl: this.querySelector('.swiper-button-prev'),
      },
    });
  }
}
if (!customElements.get('product-media-swiper')) {
  customElements.define('product-media-swiper', productMediaSwiper);
}

var RecentlyViewedProducts = class extends HTMLElement {
  async connectedCallback() {
    if (this.searchQueryString === '') {
      return;
    }
    const response = await fetch(`/search?type=product&q=${this.searchQueryString}&section_id=${this.sectionId}`);
    const div = document.createElement('div');
    div.innerHTML = await response.text();
    const recentlyViewedProductsElement = div.querySelector('recently-viewed-products');
    if (recentlyViewedProductsElement.hasChildNodes()) {
      this.innerHTML = recentlyViewedProductsElement.innerHTML;
    } else {
      this.innerHTML = '';
    }
  }
  get searchQueryString() {
    const items = JSON.parse(localStorage.getItem('theme:recently-viewed-products') || '[]');
    if (this.hasAttribute('exclude-product-id') && items.includes(parseInt(this.getAttribute('exclude-product-id')))) {
      items.splice(items.indexOf(parseInt(this.getAttribute('exclude-product-id'))), 1);
    }
    return items
      .map((item) => 'id:' + item)
      .slice(0, this.productsCount)
      .join(' OR ');
  }
  get sectionId() {
    return this.getAttribute('section-id');
  }
  get productsCount() {
    return this.getAttribute('products-count') || 4;
  }
};
window.customElements.define('recently-viewed-products', RecentlyViewedProducts);

// 1. 将初始化逻辑封装
const initScrollAnimations = () => {
  const containers = document.querySelectorAll('.js-animate-container');

  const observerOptions = {
    threshold: 0.2,
  };

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-active', 'group');
        observer.unobserve(entry.target);
      }
    });
  }, observerOptions);

  containers.forEach((container) => {
    observer.observe(container);
  });
};

// 2. 正常页面加载时执行
document.addEventListener('DOMContentLoaded', initScrollAnimations);

class ArticleToc extends HTMLElement {
  connectedCallback() {
    const contentSel = this.getAttribute('content-selector') || '.article-template__content';
    const titleSel = this.getAttribute('title-selector') || 'h2';
    const content = document.querySelector(contentSel);
    if (!content) return;

    const headings = Array.from(content.querySelectorAll(titleSel));
    if (headings.length === 0) {
      this.hidden = true;
      return;
    }

    // Shuffle and take up to 6
    const shuffled = headings
      .slice()
      .sort(() => Math.random() - 0.5)
      .slice(0, 6);
    // Re-sort by DOM order so numbering stays logical
    shuffled.sort((a, b) => (a.compareDocumentPosition(b) & 4 ? -1 : 1));

    // Ensure each heading has an id for anchor linking
    shuffled.forEach((h, i) => {
      if (!h.id) {
        h.id =
          'toc-heading-' +
          i +
          '-' +
          h.textContent
            .trim()
            .toLowerCase()
            .replace(/\s+/g, '-')
            .replace(/[^a-z0-9-]/g, '')
            .slice(0, 48);
      }
    });

    const links = shuffled
      .map(
        (h, i) =>
          `<a href="#${h.id}" class="text-[14px] md:text-[15px] hover:opacity-60 transition-opacity">${i + 1} - <span class="underline">${h.textContent.trim()}</span></a>`,
      )
      .join('');

    this.innerHTML = links;
  }
}
customElements.define('article-toc', ArticleToc);

// 3. 关键：针对 Shopify 后台配置模式的修复
// 当用户在后台修改了某个 Section 的设置，该 Section 重新加载后会触发此事件
document.addEventListener('shopify:section:load', (event) => {
  // 我们只需要针对被重新加载的那个 Section 重新运行初始化
  initScrollAnimations();
});

document.addEventListener('alpine:init', () => {
  Alpine.data('tabsComponent', (initialTab) => ({
    activeTab: initialTab,
    faqActive: null,
    sliderWidth: 0,
    sliderOffset: 0,

    updateSlider(tab) {
      if (tab) {
        this.activeTab = tab;
      }

      this.$nextTick(() => {
        const el = this.$refs[this.activeTab];

        if (el) {
          this.sliderWidth = el.offsetWidth;
          this.sliderOffset = el.offsetLeft;

          const container = el.parentElement;
          container.scrollTo({
            left: el.offsetLeft - container.offsetWidth / 2 + el.offsetWidth / 2,
            behavior: 'smooth',
          });
        }
      });
    },

    tabButton(tabName) {
      return {
        ['@click']() {
          this.updateSlider(tabName);
        },
        [':class']() {
          return this.activeTab === tabName ? 'text-[#25282A]' : 'text-[#A6A6A6] hover:text-black';
        },
      };
    },
  }));

  // 接收一个 options 对象，并给个默认空对象防报错
  Alpine.data('swiperTabsComponent', (options = {}) => ({
    // 如果没传 initialTab，默认给 0
    activeTab: `tab_${options.initialTab ?? 0}`,
    sliderWidth: 0,
    sliderOffset: 0,

    init() {
      // 触发传入的 onInit
      if (typeof options.onInit === 'function') {
        options.onInit(this);
      }

      this.$nextTick(() => {
        this.updateSlider(this.activeTab, true);
      });
    },

    updateSlider(tab, syncExternal = true) {
      if (tab) {
        this.activeTab = tab;
      }

      const tabIndex = parseInt(this.activeTab.split('_')[1], 10);

      // 触发传入的 onUpdate
      if (syncExternal && typeof options.onUpdate === 'function') {
        options.onUpdate(this, tabIndex);
      }

      this.$nextTick(() => {
        const el = this.$refs[this.activeTab];

        if (el) {
          this.sliderWidth = el.offsetWidth;
          this.sliderOffset = el.offsetLeft;

          const container = el.parentElement;
          container.scrollTo({
            left: el.offsetLeft - container.offsetWidth / 2 + el.offsetWidth / 2,
            behavior: 'smooth',
          });
        }
      });
    },

    tabButton(tabName) {
      return {
        ['@click']() {
          this.updateSlider(tabName, true);
        },
        [':class']() {
          return this.activeTab === tabName ? 'text-[#25282A]' : 'text-[#A6A6A6] hover:text-black';
        },
      };
    },
  }));
});

// 锚点平滑滚动
function initAnchorScrollLinks() {
  document.querySelectorAll('a[data-anchor-id]').forEach((link) => {
    if (link.dataset.anchorScrollBound === 'true') return;

    link.dataset.anchorScrollBound = 'true';
    link.addEventListener('click', (e) => {
      const targetId = link.dataset.anchorId;
      if (!targetId) return;

      e.preventDefault();

      const target = document.querySelector(`[data-anchor-target-id='${targetId}']`);
      if (!target) return;

      const stickyHeader = document.querySelector('store-header');
      const anchors = document.querySelector('.sa-anchors');
      const headerOffset = stickyHeader ? stickyHeader.offsetHeight : 0;
      const anchorsOffset = anchors ? anchors.offsetHeight : 0;
      const targetTop = target.getBoundingClientRect().top + window.scrollY - headerOffset - anchorsOffset;
      window.scrollTo({
        top: Math.max(targetTop, 0),
        behavior: 'smooth',
      });
    });
  });
}

document.addEventListener('DOMContentLoaded', initAnchorScrollLinks);
document.addEventListener('shopify:section:load', initAnchorScrollLinks);

// Module nav: first matching label is shown via CSS on <html data-active-module>
function getSwiperElements(container) {
  const elements = [];

  if (container.classList?.contains('swiper')) {
    elements.push(container);
  }

  container.querySelectorAll('.swiper').forEach((el) => {
    if (!elements.includes(el)) {
      elements.push(el);
    }
  });

  return elements;
}

function updateSwipersForActiveModule(label) {
  document.querySelectorAll('[data-module-label]').forEach((block) => {
    if (block.dataset.moduleLabel !== label) return;
    if (block.closest('[data-module-nav]') || block.matches('[data-module-nav-item]')) return;
    getSwiperElements(block).forEach((el) => {
      if (!el.swiper) return;
      el.swiper.update();
      el.swiper.updateSize();
      if (el.swiper.params.autoplay && el.swiper.autoplay && !el.swiper.autoplay.running) {
        el.swiper.autoplay.start();
      }
    });
  });
}

function setActiveModuleLabel(label) {
  if (!label) return;

  document.documentElement.setAttribute('data-active-module', label);

  requestAnimationFrame(() => {
    updateSwipersForActiveModule(label);
  });

  document.dispatchEvent(
    new CustomEvent('module-label:change', {
      detail: { label },
    }),
  );
}

function initModuleLabelViews() {
  const navSection = document.querySelector('[data-module-nav]');
  if (!navSection) return;

  const activeItem =
    navSection.querySelector('[data-module-nav-item][data-module-label].is-active') ||
    navSection.querySelector('[data-module-nav-item][data-module-label]');

  if (activeItem) {
    setActiveModuleLabel(activeItem.dataset.moduleLabel);
  }
}

window.setActiveModuleLabel = setActiveModuleLabel;

document.addEventListener('DOMContentLoaded', initModuleLabelViews);
document.addEventListener('shopify:section:load', initModuleLabelViews);
